import { FC } from 'react';

import { applyDictionaryToString } from '@belong/contentful';
import { FLEX_ALIGN_VERTICAL, FONT_COLOURS } from '@belong/themes';
import { FlexAlignValue, IDictionary, ITileHeader, STATUS_INDICATOR_VARIANT } from '@belong/types';

import { TILE_HEADER } from '../../../helpers/testIds';
import { StatusIndicator, Tag } from '../../feedback';
import { Icon } from '../../Icon';
import { A11yCopy, Heading, RichText } from '../../styles/Typography';
import { Inline } from '../../ui';
import * as Header from './TileHeader.styles';

export interface ITileHeaderProps extends ITileHeader {
  titleId?: string;
  additionalInfoId?: string;
  disabled?: boolean;
  isSmallImg?: boolean;
  verticalAlign?: FlexAlignValue;
  dictionary?: IDictionary;
}

interface IAdditionalInfoProps extends Pick<ITileHeaderProps, 'additionalInfo' | 'dictionary'> {
  withStatus: boolean;
  id: string;
}

const AdditionalInfo: FC<IAdditionalInfoProps> = ({ additionalInfo, dictionary, withStatus }) => {
  if (!additionalInfo) {
    return null;
  }

  if (typeof additionalInfo === 'string') {
    return (
      <Header.AdditionalInfo hasColor={FONT_COLOURS.FINE_PRINT} withStatus={withStatus}>
        {additionalInfo}
      </Header.AdditionalInfo>
    );
  }
  return (
    <Header.AdditionalInfoRichText withStatus={withStatus}>
      <RichText {...additionalInfo} hasColor={FONT_COLOURS.FINE_PRINT} dictionary={dictionary} />
    </Header.AdditionalInfoRichText>
  );
};
AdditionalInfo.displayName = 'TileHeaderAdditionalInfo';

export const TileHeader = ({
  a11yTitle,
  a11yPrefixStatusIndicator,
  additionalInfo,
  dictionary = {},
  disabled = false,
  headingVariant = 'small',
  image,
  icon,
  isSmallImg = false,
  statusIndicator,
  withStatus = true,
  subtitle,
  tag,
  title,
  value,
  verticalAlign = FLEX_ALIGN_VERTICAL.top,
  imageAriaHidden = true,
  titleId = '',
  additionalInfoId = '',
  ...wrapperProps
}: ITileHeaderProps) => {
  return (
    <Header.Wrapper verticalAlign={verticalAlign} {...wrapperProps}>
      {image && (
        <Header.Image
          src={[image.src, {}]}
          alt={image.alt}
          disabled={disabled}
          isSmallImg={isSmallImg}
          contentType={image.contentType}
          imageAriaHidden={imageAriaHidden}
        />
      )}

      {icon && !image && <Icon name={icon} size="large" />}

      <Header.Content disabled={disabled} withStatus={withStatus}>
        <Heading as="h2" id={titleId} variant={headingVariant}>
          <Inline as="span" space="xsmall" alignY="center">
            {applyDictionaryToString(dictionary, title)}
            <A11yCopy>{a11yTitle}</A11yCopy>
            {tag && <Tag data-testid={TILE_HEADER.TAG} {...tag} />}
          </Inline>
        </Heading>
        {subtitle && <Header.Subtitle variant="large">{applyDictionaryToString(dictionary, subtitle)}</Header.Subtitle>}
        <AdditionalInfo
          id={additionalInfoId}
          additionalInfo={additionalInfo}
          dictionary={dictionary}
          withStatus={withStatus}
        />
      </Header.Content>

      {withStatus && value && (
        <Header.Status data-testid={`${wrapperProps['data-testid']}-status-pill`}>
          {statusIndicator ? (
            <StatusIndicator
              status={statusIndicator}
              a11yPrefixStatusIndicator={a11yPrefixStatusIndicator}
              type={STATUS_INDICATOR_VARIANT.STATUS_PILL}
            >
              {value}
            </StatusIndicator>
          ) : (
            value
          )}
        </Header.Status>
      )}
    </Header.Wrapper>
  );
};

TileHeader.displayName = 'TileHeader';
